import {
  buildActiveFilterDescriptors,
  resolveActiveFilterColumnVisibilityToggle,
} from "@/components/data-table/utils/activeFilterDescriptors";
import { buildActiveFiltersBarCountLabel } from "@/components/data-table/utils/buildActiveFiltersBarCountLabel";
import { resolveActiveFiltersStripTone } from "@/components/data-table/components/DataTableActiveFiltersBar/activeFiltersBarShared";
import { useDataTableQuery } from "@/components/data-table/hooks/useDataTableQuery";
import { cn } from "@/lib/utils";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { Info, XCircle } from "lucide-react";
import { PropsWithChildren, memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useTableColumnVisibility from "@/components/data-table/hooks/useTableColumnVisibility";
import { useDataTableSettings } from "../../data-table/hooks/useDataTableSettings";
import { QueryTableFilteredSortedBadge } from "./QueryTableFilteredSortedBadge";
import { QueryTableLoadMoreButton } from "./QueryTableLoadMoreButton";

interface QueryTableStatusProps extends PropsWithChildren {
  className?: string;
}

const QueryTableStatusComp = <TData extends { id: string }>({
  className,
  children,
}: QueryTableStatusProps) => {
  const { t, i18n } = useTranslation();
  const { options, table, features } = useDataTableSettings<TData>();
  const { query } = useDataTableQuery<TData>();
  const { columnVisibility } = useTableColumnVisibility();

  const filterDescriptors = useMemo(
    () =>
      buildActiveFilterDescriptors(table, options.rawFilters, columnVisibility),
    [table, options.rawFilters, columnVisibility],
  );
  const committedFilterCount = filterDescriptors.length;

  /** Match global `isFiltered()` / `isSorted()` so the pill never renders empty segments. */
  const showFilterSegment = options.isFiltered();
  const showSortSegment = options.isSorted();

  const sortPrimary = options.sorting[0];
  const footerBadgeSortDirection = useMemo<"asc" | "desc" | null>(() => {
    if (!sortPrimary) return null;
    return sortPrimary.desc ? "desc" : "asc";
  }, [sortPrimary]);

  const hiddenCommittedFilterCount = useMemo(
    () => filterDescriptors.filter((d) => d.hidden).length,
    [filterDescriptors],
  );

  const filterCombinedLabel = useMemo(
    () =>
      buildActiveFiltersBarCountLabel({
        t,
        i18n,
        totalCommittedFilterCount: filterDescriptors.length,
        hiddenCommittedFilterCount,
      }),
    [filterDescriptors.length, hiddenCommittedFilterCount, i18n, t],
  );

  const sortDetailLine = useMemo(() => {
    if (!sortPrimary) return null;
    const col = table.getColumn(sortPrimary.id);
    const headerTranslationKey =
      typeof col?.columnDef.header === "string"
        ? col.columnDef.header
        : undefined;
    const column = headerTranslationKey
      ? t(headerTranslationKey)
      : sortPrimary.id;
    const direction = sortPrimary.desc
      ? t("table.sortDescending")
      : t("table.sortAscending");
    return t("table.activeFilters.sortingApplied", {
      column,
      direction,
    });
  }, [sortPrimary, table, t]);

  const footerBadgeTone = resolveActiveFiltersStripTone({
    hasHiddenCommittedFilter: hiddenCommittedFilterCount > 0,
  });

  const clearFiltersWithOptions = useCallback(() => {
    options.resetFilters();
  }, [options]);

  const clearSortingWithOptions = useCallback(() => {
    options.resetSorting();
  }, [options]);

  const getColumnVisibilityToggle = useCallback(
    (columnId: string) =>
      resolveActiveFilterColumnVisibilityToggle(table, columnId),
    [table],
  );

  const defaultClassName = "flex items-center gap-4 text-sm text-slate-600";

  if (query.isError) {
    return (
      <span className={cn(defaultClassName, className)}>
        <XCircle className="w-6 h-6 shrink-0" />
        <span>{t("table.error")}</span>
      </span>
    );
  }

  const isPaginated = query.data !== undefined && "pages" in query.data;

  const infiniteQuery = isPaginated
    ? (query as UseInfiniteQueryResult<InfiniteData<TData>>)
    : null;

  return (
    <span
      className={cn(
        "flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-600",
        defaultClassName,
        className,
      )}
    >
      {infiniteQuery && (
        <QueryTableLoadMoreButton
          isFetching={infiniteQuery.isFetching}
          hasNextPage={infiniteQuery.hasNextPage}
          fetchNextPage={infiniteQuery.fetchNextPage}
        />
      )}
      {children}
      <span className="flex items-center gap-2 shrink-0">
        <Info className="w-4 h-4" />
        <span>{t("table.status", { count: table.getRowCount() })}</span>
      </span>
      {features.enableActiveFiltersStatus &&
      options.isFilteredOrSorted() &&
      !query.isLoading ? (
        <QueryTableFilteredSortedBadge
          tone={footerBadgeTone}
          showFilterSegment={showFilterSegment}
          showSortSegment={showSortSegment}
          sortDirection={footerBadgeSortDirection}
          committedFilterCount={committedFilterCount}
          filterCombinedLabel={filterCombinedLabel}
          filterPendingHint={t("table.footer.filterPendingHint")}
          sortDetailLine={sortDetailLine}
          descriptors={filterDescriptors}
          onRemoveColumnFilter={options.resetFilterById}
          onClearFilters={clearFiltersWithOptions}
          onClearSorting={clearSortingWithOptions}
          getColumnVisibilityToggle={getColumnVisibilityToggle}
        />
      ) : null}
    </span>
  );
};

export const QueryTableStatus = memo(QueryTableStatusComp);
